import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'

import { PageHeader, StatCardGroup, StatCard } from '@/components/shared'
import { authorService } from '@/services/authorService'
import { AuthorToolbar } from '@/components/AdminPanel/Author/AuthorToolbar'
import { AuthorList } from '@/components/AdminPanel/Author/AuthorList'
import { Users } from 'lucide-react'

function authorMatchesSearch(author, query) {
  const value = query.trim().toLowerCase()
  if (!value) return true
  return [author.full_name, author.email, author.contact_number]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(value))
}

const AuthorManager = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [confirmingAuthorId, setConfirmingAuthorId] = useState(null)

  const { data, isLoading } = useQuery('authors', authorService.getAllAuthors, {
    onError: (err) => toast.error('Failed to fetch authors: ' + (err.response?.data?.error || err.message))
  })

  const authors = data?.data?.authors || []
  const filteredAuthors = useMemo(() => authors.filter((author) => authorMatchesSearch(author, searchQuery)), [authors, searchQuery])

  async function handleDelete(id) {
    try {
      await authorService.deleteAuthor(id)
      toast.success('Author deleted')
      setConfirmingAuthorId(null)
      queryClient.invalidateQueries('authors')
    } catch (err) {
      toast.error('Failed to delete author: ' + (err.response?.data?.error || err.message))
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Authors"
        description="Manage author profiles for blog posts"
        actions={
          <AuthorToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onClearSearch={() => setSearchQuery('')}
            onRefresh={() => queryClient.invalidateQueries('authors')}
            onNewAuthor={() => navigate('/admin-panel/authors/new')}
          />
        }
      />

      <StatCardGroup>
        <StatCard title="Total Authors" value={authors.length} description="All author profiles" icon={Users} contentClassName="p-4" />
      </StatCardGroup>

      <AuthorList
        authors={filteredAuthors}
        loading={isLoading}
        emptyMessage={searchQuery ? `No authors match "${searchQuery}".` : 'No authors yet.'}
        confirmingAuthorId={confirmingAuthorId}
        onEdit={(author) => navigate(`/admin-panel/authors/edit/${author.id}`)}
        onAskDelete={(id) => setConfirmingAuthorId((current) => (current === id ? null : id))}
        onCancelDelete={() => setConfirmingAuthorId(null)}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default AuthorManager
